import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { RiFileTextLine } from 'react-icons/ri';

interface CampaignData {
  date: string;
  emailSent: number;
  openRate: number;
  clickRate: number;
  ctor: number;
}

interface CampaignMetricsProps {
  campaignData: CampaignData[] | { range: string; points: any[] };
  timeRange?: string;
  onTimeRangeChange?: (range: string) => void;
}

const RiFileTextLineComponent =
  RiFileTextLine as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

const CampaignMetrics: React.FC<CampaignMetricsProps> = ({
  campaignData,
  timeRange = 'last-30-days',
  onTimeRangeChange
}) => {
  // Local state for dropdown if parent doesn't control it
  const [localTimeRange, setLocalTimeRange] = useState(timeRange);

  // Update local state when prop changes
  useEffect(() => {
    setLocalTimeRange(timeRange);
  }, [timeRange]);

  let dataArray: CampaignData[] = [];

  if (Array.isArray(campaignData)) {
    dataArray = campaignData;
  } else if (campaignData?.points) {
    // Map API response to CampaignData format
    dataArray = campaignData.points.map((point: any) => ({
      date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      emailSent: point.emailSent || point.emailsent || 0,
      openRate: point.openRate || 0,
      clickRate: point.clickRate || 0,
      ctor: point.ctor || 0,
    }));
  }

  const hasData = dataArray.length > 0;

  // Use local filter for display
  const effectiveFilter = localTimeRange === 'last-7-days' ? '7' :
    localTimeRange === 'last-1-day' ? '1' :
      localTimeRange === 'last-1-year' ? '365' : '30';

  const filteredData = hasData
    ? (effectiveFilter === '7' ? dataArray.slice(0, 7) :
      effectiveFilter === '1' ? dataArray.slice(0, 1) :
        effectiveFilter === '365' ? dataArray : dataArray)
    : [];

  // Handle dropdown change
  const handleRangeChange = (value: string) => {
    setLocalTimeRange(value);
    if (onTimeRangeChange) {
      onTimeRangeChange(value);
    }
  };

  // Show empty state if no data
  if (!hasData) {
    return (
      <div className='bg-white p-4 rounded-lg border border-gray-200 shadow-sm'>
        <div className='flex justify-between items-center mb-4'>
          <h3
            className='text-lg font-semibold'
            style={{ color: '#3F5265', fontSize: '20px' }}
          >
            Campaign Metrics
          </h3>
          <select
            className='border rounded p-2'
            style={{
              borderColor: '#00A4BD',
              color: '#00A4BD',
              borderWidth: '2px',
            }}
            value={localTimeRange}
            onChange={(e) => handleRangeChange(e.target.value)}
          >
            <option value='last-1-day'>Last 24 hours</option>
            <option value='last-7-days'>Last 7 days</option>
            <option value='last-30-days'>Last 30 days</option>
            <option value='last-1-year'>Last 1 year</option>
          </select>
        </div>

        <div className='flex flex-col items-center justify-center py-20 text-gray-500'>
          <RiFileTextLineComponent size={48} className='mb-6 text-gray-400' />
          <p className='text-base mb-1 text-center'>
            Your campaign performance metrics will appear here.
          </p>
          <p className='text-base mb-8 text-center'>
            Start by launching your first campaign.
          </p>
        </div>
      </div>
    );
  }

  // Normal chart view when data exists
  const categories = filteredData.map((d) => d.date);

  const series = [
    { name: 'Email sent', data: filteredData.map((d) => d.emailSent / 15) },
    { name: 'Open rate', data: filteredData.map((d) => d.openRate) },
    { name: 'Click rate', data: filteredData.map((d) => d.clickRate * 4) },
    { name: 'CTOR', data: filteredData.map((d) => d.ctor) },
  ];

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2 },
    markers: { size: 5, strokeWidth: 2, hover: { size: 7 } },
    colors: ['#00E396', '#FF5733', '#008FFB', '#D633FF'],
    xaxis: {
      categories,
      labels: { style: { fontSize: '12px', colors: '#657584' } },
    },
    yaxis: {
      labels: {
        formatter: (val) => `${Math.round(val)}%`,
        style: { fontSize: '12px', colors: '#657584' },
      },
      min: 0,
      max: 100,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '14px',
      markers: { shape: 'square' },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => `${val.toFixed(0)}%` },
    },
    grid: { borderColor: '#f1f1f1' },
  };

  return (
    <div className='bg-white p-4 rounded-lg border border-gray-200 shadow-sm'>
      <div className='flex justify-between items-center mb-4'>
        <h3
          className='text-lg font-semibold'
          style={{ color: '#3F5265', fontSize: '20px' }}
        >
          Campaign Metrics
        </h3>
        <select
          className='border rounded p-2'
          style={{
            borderColor: '#00A4BD',
            color: '#00A4BD',
            borderWidth: '2px',
          }}
          value={localTimeRange}
          onChange={(e) => handleRangeChange(e.target.value)}
        >
          <option value='last-1-day'>Last 24 hours</option>
          <option value='last-7-days'>Last 7 days</option>
          <option value='last-30-days'>Last 30 days</option>
          <option value='last-1-year'>Last 1 year</option>
        </select>
      </div>

      <Chart
        options={options}
        series={series}
        type='line'
        height={300}
      />
    </div>
  );
};

export default CampaignMetrics;